// Team view: a single-day view with one column per active team member.
// Events render in their owner's column based on the invitees list.
// Drag-create in a column auto-fills the booking modal's invitees with that
// member's email so the new event lands in the right column.

import { freshBooking } from '../../functions/calendar/calendarUtils.js'

const HOUR_PX = 56

export const CalTeamView = {
  flow: 'y',
  flex: '1',
  overflow: 'hidden',
  userSelect: 'none',
  display: (el, s) => s.root.viewMode === 'team' ? 'flex' : 'none',

  DayHeaders: {
    flow: 'x',
    borderBottomWidth: '1px',
    borderBottomColor: 'line',
    borderBottomStyle: 'solid',
    minHeight: 'D1',

    TimeGutter: {
      width: 'E',
      minWidth: 'E',
      borderRightWidth: '1px',
      borderRightColor: 'line',
      borderRightStyle: 'solid',
      userSelect: 'none'
    },

    Members: {
      flex: '1',
      flow: 'x',
      overflow: 'hidden',

      childExtends: 'CalTeamMemberHeader',
      childrenAs: 'state',
      children: (el, s) => {
        const root = s.root
        // Always exclude advisors from the calendar team views.
        const all = el.call('activeMembers')
        const sel = root.teamMembers || []
        // Empty teamMembers list = "show everyone" (no per-user filter set yet).
        if (!sel.length) return all
        const visible = new Set(sel)
        return all.filter(p => visible.has(p.email))
      }
    }
  },

  ScrollBody: {
    flex: '1',
    overflow: 'auto',
    position: 'relative',
    flow: 'x',
    userSelect: 'none',
    alignItems: 'flex-start',

    // Live time-on-hover: derive the hovered HH:MM from cursor Y inside the
    // scroll body and write it into root.hoverTime. Member-column headers
    // reactively re-format that into each person's local timezone.
    onMousemove: (e, el, s) => {
      const rect = el.node.getBoundingClientRect()
      // Account for the parent's scrollTop because we want grid-relative Y
      const offsetY = (e.clientY - rect.top) + el.node.scrollTop
      const t = el.call('pxToTime', offsetY)
      if (s.root.hoverTime !== t) {
        s.root.update({ hoverTime: t })
      }
    },
    onMouseleave: (e, el, s) => {
      if (s.root.hoverTime != null) s.root.update({ hoverTime: null })
    },

    TimeGutter: {
      width: 'E',
      minWidth: 'E',
      borderRightWidth: '1px',
      borderRightColor: 'line',
      borderRightStyle: 'solid',
      flow: 'y',
      cursor: 'cell',
      userSelect: 'none',

      // Drag in the time gutter → company-wide event spanning all visible
      // team members. We attach the move/up listeners to the document on
      // mousedown so the gesture survives the cursor leaving the narrow
      // gutter mid-drag (a real-world hand wobble used to kill the drag).
      onMousedown: (e, el, s) => {
        if (e.target.closest('button')) return
        const node = el.node
        const rect = node.getBoundingClientRect()
        const root = s.root
        let dragMoved = false
        const startY = (e.clientY - rect.top) + node.scrollTop

        // Resolve "all visible team members" once — falls back to every
        // non-advisor person when teamMembers is empty (matches the column
        // children getter so the preview slot lights up the same set).
        const allTeam = el.call('activeMembers').map(p => p.email)
        const memberEmails = (root.teamMembers && root.teamMembers.length)
          ? root.teamMembers.slice()
          : allTeam

        const updatePreview = (curY) => {
          const y0 = Math.min(startY, curY)
          const y1 = Math.max(startY, curY)
          const startTime = el.call('pxToTime', y0)
          const endTime = el.call('pxToTime', y1)
          const finalEnd = endTime > startTime ? endTime : el.call('addMinutes', startTime, 30)
          root.update({
            dragPreview: {
              date: el.call('formatDate', root.currentYear, root.currentMonth, root.currentDay),
              startTime,
              endTime: finalEnd,
              color: 'blue',
              memberEmails
            }
          })
        }

        // Seed the preview at the click point.
        updatePreview(startY)

        const onMove = (ev) => {
          // Recompute curY relative to the gutter, not the cursor target —
          // works whether the pointer is inside or outside the gutter.
          const curY = (ev.clientY - rect.top) + node.scrollTop
          if (Math.abs(curY - startY) > 5) dragMoved = true
          updatePreview(curY)
        }

        const onUp = (ev) => {
          document.removeEventListener('mousemove', onMove)
          document.removeEventListener('mouseup', onUp)
          if (!dragMoved) { root.update({ dragPreview: null }); return }
          const endY = (ev.clientY - rect.top) + node.scrollTop
          const y0 = Math.min(startY, endY)
          const y1 = Math.max(startY, endY)
          const startTime = el.call('pxToTime', y0)
          const endTimeRaw = el.call('pxToTime', y1)
          const endTime = endTimeRaw > startTime ? endTimeRaw : el.call('addMinutes', startTime, 30)
          const allTeam = el.call('activeMembers').map(p => p.email)
          const sel = (root.teamMembers && root.teamMembers.length)
            ? root.teamMembers
            : allTeam
          root.update({
            dragPreview: null,
            showNewBooking: true,
            newBooking: freshBooking({
              date: el.call('formatDate', root.currentYear, root.currentMonth, root.currentDay),
              startTime,
              endTime,
              invitees: sel.join(', '),
              type: 'general'
            })
          })
        }

        document.addEventListener('mousemove', onMove)
        document.addEventListener('mouseup', onUp)
      },

      childExtends: 'CalTimeLabel',
      childrenAs: 'state',
      children: (el) => el.call('getHours').map((h, i) => ({ label: h.label, h: h.h }))
    },

    // Wrapper holds the iterated columns + a non-iterated NowLineWrap.
    // (childrenAs would otherwise clobber any named child here.)
    MemberColumnsWrap: {
      flex: '1',
      flow: 'y',
      position: 'relative',

      MemberColumns: {
        flex: '1',
        flow: 'x',
        position: 'relative',

        // Horizontal drag-create across multiple member columns.
        // - Tracks start + current column INDEX via the dataset attribute
        //   each CalTeamMemberColumn writes (`data-cal-team-col-email`).
        // - Drag preview lights up every column whose email is in the
        //   spanned range.
        // - On mouseup, opens NewBookingModal with invitees = comma-joined
        //   emails of the spanned members.
        onMousedown: (e, el, s) => {
          if (e.target.closest('button, [data-cal-event-id]')) return
          const colNode = e.target.closest('[data-cal-team-col-email]')
          if (!colNode) return
          const cols = Array.from(el.node.querySelectorAll('[data-cal-team-col-email]'))
          const idx = cols.indexOf(colNode)
          if (idx === -1) return
          const colRect = colNode.getBoundingClientRect()
          el._cols = cols
          el._startIdx = idx
          el._curIdx = idx
          el._startY = e.clientY - colRect.top
          el._curY = el._startY
          el._dragMoved = false
          const startTime = el.call('pxToTime', el._startY)
          const root = s.root
          root.update({
            dragPreview: {
              date: el.call('formatDate', root.currentYear, root.currentMonth, root.currentDay),
              startTime,
              endTime: el.call('addMinutes', startTime, 30),
              color: root.newBooking?.color || 'blue',
              memberEmails: [colNode.dataset.calTeamColEmail]
            }
          })
        },
        onMousemove: (e, el, s) => {
          if (el._cols == null) return
          // Find current column under cursor (snap to existing one if outside)
          let curCol = document.elementFromPoint(e.clientX, e.clientY)
          curCol = curCol?.closest?.('[data-cal-team-col-email]')
          if (curCol && el._cols.indexOf(curCol) !== -1) {
            el._curIdx = el._cols.indexOf(curCol)
          }
          const startCol = el._cols[el._startIdx]
          const startRect = startCol.getBoundingClientRect()
          el._curY = e.clientY - startRect.top
          if (Math.abs(el._curY - el._startY) > 5 || el._curIdx !== el._startIdx) el._dragMoved = true
          const y0 = Math.min(el._startY, el._curY)
          const y1 = Math.max(el._startY, el._curY)
          const startTime = el.call('pxToTime', y0)
          const endTime = el.call('pxToTime', y1)
          const finalEnd = endTime > startTime ? endTime : el.call('addMinutes', startTime, 30)
          const lo = Math.min(el._startIdx, el._curIdx)
          const hi = Math.max(el._startIdx, el._curIdx)
          const emails = el._cols.slice(lo, hi + 1).map(n => n.dataset.calTeamColEmail)
          s.root.update({
            dragPreview: {
              date: el.call('formatDate', s.root.currentYear, s.root.currentMonth, s.root.currentDay),
              startTime,
              endTime: finalEnd,
              color: s.root.newBooking?.color || 'blue',
              memberEmails: emails
            }
          })
        },
        onMouseup: (e, el, s) => {
          if (el._cols == null) return
          const startCol = el._cols[el._startIdx]
          const startRect = startCol.getBoundingClientRect()
          const startY = el._startY
          const endY = e.clientY - startRect.top
          const dragMoved = el._dragMoved
          const lo = Math.min(el._startIdx, el._curIdx)
          const hi = Math.max(el._startIdx, el._curIdx)
          const emails = el._cols.slice(lo, hi + 1).map(n => n.dataset.calTeamColEmail)
          el._cols = null
          el._startIdx = null
          el._curIdx = null
          el._dragMoved = false
          const y0 = Math.min(startY, endY)
          const y1 = Math.max(startY, endY)
          const startTime = el.call('pxToTime', y0)
          const endTimeRaw = el.call('pxToTime', y1)
          const endTime = dragMoved && endTimeRaw > startTime
            ? endTimeRaw
            : el.call('addMinutes', startTime, 30)
          const root = s.root
          root.update({
            dragPreview: null,
            showNewBooking: true,
            newBooking: freshBooking({
              date: el.call('formatDate', root.currentYear, root.currentMonth, root.currentDay),
              startTime,
              endTime,
              invitees: emails.join(', '),
              type: 'meeting'
            })
          })
        },
        onMouseleave: (e, el, s) => {
          if (el._cols != null) {
            el._cols = null
            el._startIdx = null
            el._curIdx = null
            el._dragMoved = false
            s.root.update({ dragPreview: null })
          }
        },

        childExtends: 'CalTeamMemberColumn',
        childrenAs: 'state',
        children: (el, s) => {
          const root = s.root
          void root.currentMinutes
          const all = el.call('activeMembers')
          const sel = root.teamMembers || []
          if (!sel.length) return all
          const visible = new Set(sel)
          return all.filter(p => visible.has(p.email))
        }
      },

      // Horizontal NOW line spanning all member columns at user's local
      // current minute. Sibling of MemberColumns so it isn't clobbered by
      // childrenAs iteration.
      NowLineWrap: {
        position: 'absolute',
        left: '0',
        right: '0',
        height: '0',
        zIndex: 5,
        pointerEvents: 'none',
        top: (el, s) => `${(s.root.currentMinutes / 60) * 56}px`,

        NowBar: {
          position: 'absolute',
          left: '0',
          right: '0',
          top: '0',
          height: '2px',
          transform: 'translateY(-50%)',
          background: 'red'
        },
        NowDot: {
          position: 'absolute',
          left: '0',
          top: '0',
          width: '12px',
          height: '12px',
          transform: 'translate(-50%, -50%)',
          round: '50%',
          background: 'red'
        }
      }
    }
  }
}

// Single red overlay for any unavailable slot (off-hours, busy event, or OOO).
// All three look the same — meeting events stay green chips, everything else
// is red background.
export const CalUnavailableSlot = {
  position: 'absolute',
  left: '0',
  right: '0',
  pointerEvents: 'none',
  background: 'red.12',
  top: (el, s) => `${(s.startMin / 60) * 56}px`,
  height: (el, s) => `${((s.endMin - s.startMin) / 60) * 56}px`
}

export const CalTeamMemberHeader = {
  flex: '1',
  flow: 'y',
  align: 'center center',
  gap: 'Y',
  padding: 'Y Z',
  borderRightWidth: '1px',
  borderRightColor: 'line',
  borderRightStyle: 'solid',
  minWidth: '0',
  overflow: 'hidden',
  position: 'relative',

  Avatar: {
    avatarType: 'initials',
    boxSize: 'B1 B1',
    src: (el, s) => el.call('avatarUrl', s.name)
  },

  Name: {
    tag: 'span',
    fontSize: 'Y2',
    fontWeight: '600',
    color: 'title',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    textAlign: 'center',
    text: (el, s) => (s.name || '').split(' ')[0]
  },
  LocalTime: {
    tag: 'span',
    fontSize: 'Y2',
    fontFamily: 'monospace',
    // Highlights when hovering — different color so the user can see this is
    // the converted hover time, not the live current time.
    color: (el, s) => el.state.root.hoverTime ? 'title' : 'caption',
    fontWeight: (el, s) => el.state.root.hoverTime ? '600' : '400',
    text: (el, s) => {
      if (!s.timezone) return ''
      const root = el.state.root
      let ref
      if (root.hoverTime) {
        // Treat the hovered HH:MM as the user's LOCAL wall-clock time on the
        // currently-viewed day, then format it in the member's timezone.
        const [h, m] = root.hoverTime.split(':').map(Number)
        ref = new Date(root.currentYear, root.currentMonth, root.currentDay, h, m)
      } else {
        ref = new Date()
      }
      return new Intl.DateTimeFormat('en-US', {
        timeZone: s.timezone, hour: '2-digit', minute: '2-digit', hour12: false
      }).format(ref)
    }
  },

  RemoveBtn: {
    tag: 'button',
    position: 'absolute',
    top: 'Y',
    right: 'Y',
    width: 'A',
    height: 'A',
    padding: '0',
    round: '50%',
    border: 'none',
    background: 'transparent',
    color: 'caption',
    cursor: 'pointer',
    fontSize: 'Y2',
    align: 'center center',
    ':hover': { background: 'overlay', color: 'title' },
    Icon: { icon: 'crossmark', boxSize: 'Z2' },
    onClick: (e, el, s) => {
      e.stopPropagation()
      const root = el.state.root
      const current = root.teamMembers || []
      // When teamMembers is empty it means "show everyone" — materialise
      // the full list first so removing one entry actually hides a column.
      const base = current.length
        ? current
        : el.call('activeMembers').map(p => p.email)
      const next = base.filter(em => em !== s.email)
      root.update({ teamMembers: next })
    }
  }
}

export const CalTeamMemberColumn = {
  flex: '1',
  position: 'relative',
  borderRightWidth: '1px',
  borderRightColor: 'line',
  borderRightStyle: 'solid',
  minWidth: '0',
  cursor: 'cell',
  // Used by the parent MemberColumns drag handler to identify which member
  // each column belongs to without having to iterate state.
  attr: {
    'data-cal-team-col-email': (el, s) => s.email || ''
  },

  // Double-click still creates a 60-min single-attendee booking.
  // (Single-column / horizontal drag is handled by the parent MemberColumns.)
  onDblClick: (e, el, s) => {
    if (e.target.closest('button, [data-cal-event-id]')) return
    const rect = el.node.getBoundingClientRect()
    const offsetY = e.clientY - rect.top
    const startTime = el.call('pxToTime', offsetY)
    const endTime = el.call('addMinutes', startTime, 60)
    const root = s.root
    root.update({
      showNewBooking: true,
      newBooking: freshBooking({
        date: el.call('formatDate', root.currentYear, root.currentMonth, root.currentDay),
        startTime,
        endTime,
        invitees: s.email,
        type: 'meeting'
      })
    })
  },

  HourLines: {
    flow: 'y',
    pointerEvents: 'none',
    childExtends: 'CalHourLine',
    childrenAs: 'state',
    children: (el) => el.call('getHours').map((h, i) => ({ h: i }))
  },

  // Unavailable bands (timezone-aware): converts each user-local minute back
  // to UTC, then to the member's local time/date in their tz, and checks
  // their workingHours / outOfOffice. Bands are tagged with their type so
  // CalUnavailableSlot can use a different color for OOO vs off-hours.
  Unavailable: {
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none',

    childExtends: 'CalUnavailableSlot',
    childrenAs: 'state',
    children: (el, s) => {
      const root = el.state?.root
      // Touch the reactive fields we depend on up-front so the signal graph
      // subscribes even on the first run when some fields may be transient.
      const events = root?.events || []
      const cy = root?.currentYear
      const cm = root?.currentMonth
      const cd = root?.currentDay
      if (cy == null) return []
      const baseMs = new Date(cy, cm, cd, 0, 0).getTime()
      // Same per-column scoping as the Events layer below.
      const colEvents = events.filter(ev =>
        ev.googleCalendarId === s.email || (!ev.googleCalendarId && (ev.invitees || []).includes(s.email))
      )
      return el.call('getUnavailableRanges', s, baseMs, 24 * 60, 15, colEvents)
    }
  },

  Events: {
    position: 'absolute',
    inset: '0',
    padding: '0 2px',
    pointerEvents: 'none',

    childExtends: 'CalWeekEvent',
    childrenAs: 'state',
    children: (el, s) => {
      const root = el.state.root
      const dateStr = el.call('formatDate', root.currentYear, root.currentMonth, root.currentDay)
      // Per-column scoping: show events that actually live on this column's
      // calendar (googleCalendarId === s.email) so deleting from one person's
      // calendar removes the chip from their column without affecting others.
      // Local-only events (no googleCalendarId yet) fall back to invitees.
      return (root.events || []).filter(ev =>
        ev.date === dateStr &&
        (ev.googleCalendarId === s.email || (!ev.googleCalendarId && (ev.invitees || []).includes(s.email))) &&
        (!ev.type || ev.type === 'meeting' || ev.type === 'general' || ev.type === 'busy')
      )
    }
  },

  // Live drag preview — visible in this member's column when their email is
  // in the dragPreview's memberEmails array (single column or horizontal
  // drag span). Group events from the time gutter set memberEmails to all
  // visible members, so the preview lights up everywhere.
  DragPreview: {
    display: (el, s) => {
      const dp = s.root?.dragPreview
      if (!dp) return 'none'
      const list = dp.memberEmails || []
      return list.includes(s.email) ? 'flex' : 'none'
    },
    position: 'absolute',
    left: '2px',
    right: '2px',
    round: 'Z',
    padding: 'Y2 Z',
    pointerEvents: 'none',
    zIndex: 4,
    flow: 'y',
    color: 'white',
    fontSize: 'Y2',
    fontWeight: '500',
    opacity: '.7',
    borderWidth: '2px',
    borderStyle: 'dashed',
    borderColor: 'white',
    background: (el, s) => s.root?.dragPreview?.color || 'blue',
    top: (el, s) => `${el.call('eventToTop', s.root?.dragPreview?.startTime || '00:00')}px`,
    height: (el, s) => `${el.call('eventToHeight', s.root?.dragPreview?.startTime || '00:00', s.root?.dragPreview?.endTime || '00:30')}px`,

    Time: {
      tag: 'span',
      text: (el, s) => `${s.root?.dragPreview?.startTime || ''} – ${s.root?.dragPreview?.endTime || ''}`,
      opacity: '.95'
    }
  }
}
